import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Table, Avatar, Dropdown, ThreeDots } from '@lmc/ui-kit'
import { AlignType } from 'rc-table/lib/interface'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import dayjs from 'dayjs'
import ActionCell from './ActionCell'
import Success from '../../iconsV2/Success'
import Warning from '../../iconsV2/Warning'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { Text } from '@lmc/ui-kit/src/general'
import { MedicationStockReason } from '../../types'

type StockRecordsTableProps = {
  logs: any[]
  onViewRecord: (id: string) => void
  onEditRecord: (id: string) => void
  onViewAdministrationRecord: (id: string) => void
  // onViewCommentRecord: (id: string) => void
}

export const StockRecordsTable: React.FC<StockRecordsTableProps> = ({
  logs,
  onViewRecord,
  onEditRecord,
  onViewAdministrationRecord
  // onViewCommentRecord
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()

  const tableColumns = [
    {
      title: t('TIME_RECORDED'),
      dataIndex: 'timeLogged',
      key: 'timeLogged',
      width: 140,
      render: (timeLogged: string) => (
        <Text>{dayjs(timeLogged).format('DD-MM-YYYY HH:mm')}</Text>
      )
    },
    {
      title: t('RECORDED_BY'),
      dataIndex: 'loggedBy',
      key: 'loggedBy',
      width: 200,
      render: (loggedBy: any) => {
        const carerName = `${loggedBy.name.first} ${loggedBy.name.last}`

        return (
          <RecordedByCell>
            <Avatar url={loggedBy.picture as unknown as string} size={30} />
            <CarerName>{carerName}</CarerName>
          </RecordedByCell>
        )
      }
    },
    {
      title: t('ACTION'),
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (action: string, record: any) => {
        const handleActionClick = () => {
          if (record.actionType === MedicationStockReason.MED_ADMIN) {
            onViewAdministrationRecord(record.id)
          }
          // else if (
          //   record.actionType === MedicationStockReason.NOT_ADMINISTERED
          // ) {
          //   onViewCommentRecord(record.id)
          // }
        }

        return (
          <ActionCell
            type={record.actionType}
            title={record.title}
            subtitle={record.subtitle}
            hasDiscrepancy={record.hasDiscrepancy}
            onClick={handleActionClick}
          />
        )
      }
    },
    {
      title: (
        <ColumnHeaderWithIcon>
          {t('EXPECTED_STOCK')}
          <Tooltip
            title={t('EXPECTED_STOCK_TOOLTIP')}
            color={theme.color.neutral.color3}
            overlayInnerStyle={{
              color: '#fff',
              borderRadius: '9px',
              padding: '10px 16px'
            }}
          >
            <QuestionIcon>
              <QuestionMarkIcon />
            </QuestionIcon>
          </Tooltip>
        </ColumnHeaderWithIcon>
      ),
      dataIndex: 'amount2',
      key: 'amount2',
      width: 120,
      align: 'center' as AlignType,
      render: (amount: number) => <Text>{amount}</Text>
    },
    {
      title: (
        <ColumnHeaderWithIcon>
          {t('STOCK_COUNT')}
          <Tooltip
            title={t('STOCK_COUNT_TOOLTIP')}
            color={theme.color.neutral.color3}
            overlayInnerStyle={{
              color: '#fff',
              borderRadius: '9px',
              padding: '10px 16px'
            }}
          >
            <QuestionIcon>
              <QuestionMarkIcon />
            </QuestionIcon>
          </Tooltip>
        </ColumnHeaderWithIcon>
      ),
      dataIndex: 'amount1',
      key: 'amount1',
      width: 120,
      align: 'center' as AlignType,
      render: (amount: number, record: any) => {
        const hasDiscrepancy = record.hasDiscrepancy
        const isResolved = record.isResolved || false

        return (
          <StockCountContainer>
            <StockCountValue>{amount}</StockCountValue>
            {hasDiscrepancy && !isResolved && (
              <Tooltip title={t('DISCREPANCY_DETECTED')}>
                <IconWrapper>
                  <Warning />
                </IconWrapper>
              </Tooltip>
            )}
            {hasDiscrepancy && isResolved && (
              <Tooltip
                title={
                  <TooltipContent>
                    <TooltipTitle>{t('RESOLVED')}</TooltipTitle>
                    <TooltipDivider />
                    <TooltipMessage>
                      {record.resolveMessage || t('DISCREPANCY_RESOLVED')}
                    </TooltipMessage>
                  </TooltipContent>
                }
              >
                <IconWrapper>
                  <Success />
                </IconWrapper>
              </Tooltip>
            )}
          </StockCountContainer>
        )
      }
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center' as AlignType,
      render: (_: any, record: any) => {
        const actionItems = [
          {
            key: 'view',
            label: t('VIEW_RECORD'),
            onClick: () => onViewRecord(record.id)
          }
        ]

        if (record.actionType !== MedicationStockReason.MED_ADMIN) {
          actionItems.push({
            key: 'edit',
            label: t('EDIT_RECORD'),
            onClick: () => onEditRecord(record.id)
          })
        }

        return (
          <ActionsContainer>
            <Dropdown
              menu={{ items: actionItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <ClickableIconWrapper>
                <ThreeDots horizontal />
              </ClickableIconWrapper>
            </Dropdown>
          </ActionsContainer>
        )
      }
    }
  ]

  return (
    <Table
      columns={tableColumns}
      dataSource={logs}
      rowKey="id"
      pagination={{ pageSize: 10, showSizeChanger: false }}
      data-testid="med-stock-logs-table-v2"
    />
  )
}

const RecordedByCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CarerName = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
`

const StockCountValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
`

const ColumnHeaderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const StockCountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const IconWrapper = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  width: 14px;
`

const TooltipContent = styled.div`
  padding: 10px 16px;
`

const TooltipTitle = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`

const TooltipDivider = styled.div`
  height: 1px;
  background-color: #d9d9d9;
  margin: 4px 0;
`

const TooltipMessage = styled.div`
  font-size: 14px;
`

const ClickableIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color7};
    border-radius: 4px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`
